import { apiFetch } from '@/lib/http/client';
import { isNative, isMobile } from '@/lib/platform';
import { useWorkspaceStore } from '@/stores/use-workspace-store';

/**
 * Opens the OAuth flow for a calendar provider in a cross-platform way.
 *
 * - Desktop (production): opens system browser via plugin-opener, passes
 *   `nula://calendar-connected` as redirect_to so the deep link fires back
 *   into the app when the flow completes.
 * - Web / dev: navigates the current window to the OAuth URL; the server
 *   redirects back to `{origin}/calendar` when done.
 *
 * Throws if no workspace is active. The server-side WorkspaceContext extractor
 * falls back to the user's personal workspace when X-Workspace-ID is absent,
 * which would silently land the connection in the wrong place. An explicit
 * error here is better than a silent workspace mismatch.
 */
export async function openCalendarOAuth(provider: 'google' | 'microsoft'): Promise<void> {
  const activeWorkspaceId = useWorkspaceStore.getState().activeWorkspaceId;
  if (activeWorkspaceId === null) {
    throw new Error('No active workspace — cannot start calendar OAuth flow.');
  }

  // Use system browser when: production desktop, or any mobile (WebView blocks Google OAuth)
  const useSystemBrowser = isNative && (!import.meta.env.DEV || isMobile);

  const redirectTo = useSystemBrowser
    ? 'nula://calendar-connected'
    : window.location.origin;

  const { url } = await apiFetch<{ url: string }>(
    `/workspace/integrations/calendar/oauth/${provider}?redirect_to=${encodeURIComponent(redirectTo)}`
  );

  if (useSystemBrowser) {
    const { openUrl } = await import('@tauri-apps/plugin-opener');
    await openUrl(url);
  } else {
    window.location.href = url;
  }
}
